import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { RedactionSummary } from "./generated/api.schemas";

// ─── Mark record as entered into Sentral ─────────────────────────────────────
// Hand-rolled because the generated `useSetRecordEntered` doesn't invalidate
// the records list. We keep this wrapper for now so existing call sites don't
// break, and it forwards onSuccess invalidation to keep lists fresh.

export function useMarkRecordEntered() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, entered }: { recordId: number; entered: boolean }) =>
      customFetch(`/api/records/${recordId}/entered`, {
        method: "PATCH",
        body: JSON.stringify({ entered }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/records"] });
    },
  });
}

// ─── NCCD-specific AI polish (returns polished text + detected adjustment IDs) ─
// Not yet covered by the OpenAPI spec; lives here until that route is added.

export interface NccdPolishResult {
  polished: string;
  adjustments: string[];
  original: string;
  /** Task #93 — what the redaction layer removed before sending to AI. */
  redaction?: RedactionSummary;
  /** Task #140 — id of the ai_usage_monthly row this call recorded
   *  against, so the UI can attach 👍/👎 feedback. */
  aiUsageId?: number | null;
}

export function useNccdPolish() {
  return useMutation({
    mutationFn: ({
      text,
      studentId,
      adjustmentIds,
    }: {
      text: string;
      /** Stable id for the named student (when known). The server
       *  looks up the display name from its own records — raw names
       *  are never accepted from the client (Task #158). */
      studentId?: number;
      adjustmentIds: string[];
    }): Promise<NccdPolishResult> =>
      customFetch("/api/ai/nccd-polish", {
        method: "POST",
        body: JSON.stringify({ text, studentId, adjustmentIds }),
      }),
  });
}

// ─── Personal NCCD adjustment bank (usage history) ───────────────────────────
// Not yet covered by the OpenAPI spec; lives here until that route is added.

export interface PersonalAdjustmentEntry {
  id: string;
  count: number;
}

export interface PersonalAdjustmentsResult {
  adjustments: PersonalAdjustmentEntry[];
  totalRecords: number;
}

export function usePersonalNccdAdjustments() {
  return useQuery<PersonalAdjustmentsResult>({
    queryKey: ["/api/nccd-adjustments/personal"],
    queryFn: () => customFetch("/api/nccd-adjustments/personal"),
  });
}

// ─── Adjustment analytics ─────────────────────────────────────────────────────
// Not yet covered by the OpenAPI spec; lives here until that route is added.

export interface AdjustmentAnalyticsItem { id: string; count: number; }
export interface AdjustmentStudentRow {
  studentId: number;
  studentName: string;
  nccdRecordCount: number;
  adjustments: AdjustmentAnalyticsItem[];
}
export interface AdjustmentClassRow {
  classId: number;
  className: string;
  nccdRecordCount: number;
  adjustments: AdjustmentAnalyticsItem[];
}
export interface AdjustmentAnalytics {
  totalNccdRecords: number;
  overallAdjustments: AdjustmentAnalyticsItem[];
  byStudent: AdjustmentStudentRow[];
  byClass: AdjustmentClassRow[];
}

export function useGetAdjustmentAnalytics(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.set("startDate", startDate);
  if (endDate) params.set("endDate", endDate);
  return useQuery<AdjustmentAnalytics>({
    queryKey: ["/api/analytics/adjustments", startDate, endDate],
    queryFn: () => customFetch(`/api/analytics/adjustments?${params.toString()}`),
  });
}
